import React, { useEffect, useState } from 'react';
import { FileDown } from 'lucide-react';
import { useData } from '../../App';
import { Badge } from '../UI';
import { ALL_ORGS, BillingStatus, Contract, EndCustomer, ProjectStatus } from '../../types';
import { api } from '../../services/api';
import { formatDay, orgDisplayName } from '../../utils/format';

export const PROJECT_STATUS: Record<ProjectStatus, { label: string; variant: 'success' | 'warning' | 'neutral' }> = {
    IN_PROGRESS: { label: 'В работе', variant: 'warning' }, DONE: { label: 'Сдан', variant: 'success' }, CLOSED: { label: 'Закрыт', variant: 'neutral' },
};

export const BILLING_STATUS: Record<BillingStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    NOT_FORMALIZED: { label: 'Не оформлено', variant: 'danger' },
    IN_APPENDIX: { label: 'В приложении', variant: 'neutral' },
    INVOICED: { label: 'Выставлен счёт', variant: 'warning' },
    PARTIALLY_PAID: { label: 'Частично оплачено', variant: 'warning' },
    PAID: { label: 'Оплачено', variant: 'success' },
};

export const StatusBadge: React.FC<{ status: BillingStatus }> = ({ status }) =>
    <Badge variant={BILLING_STATUS[status].variant}>{BILLING_STATUS[status].label}</Badge>;

// Суммы в таблицах: пробелы между разрядами, без «₽» (единица — в заголовке)
export const money = (n: number) => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export const Money: React.FC<{ value: number; tone?: 'debt' | 'good' | 'muted' }> = ({ value, tone }) => {
    const cls = tone === 'debt' && value > 0.004 ? 'text-red-600 font-semibold'
        : tone === 'good' && value > 0 ? 'text-emerald-700' : tone === 'muted' ? 'text-slate-400' : '';
    return <span className={`font-mono tabular-nums whitespace-nowrap ${cls}`}>{money(value)}</span>;
};

// Справочники расчётов: договоры и конечные заказчики
export const useBillingRefs = () => {
    const { revision } = useData();
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [endCustomers, setEndCustomers] = useState<EndCustomer[]>([]);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let alive = true;
        api.getContracts().then(c => { if (alive) setContracts(c); }).catch(() => {});
        api.getEndCustomers().then(e => { if (alive) setEndCustomers(e); }).catch(() => {});
        return () => { alive = false; };
    }, [revision, tick]);
    return { contracts, endCustomers, reloadRefs: () => setTick(t => t + 1) };
};

// Охват из шапки: конкретный ИП или все
export const useScope = () => {
    const { state } = useData();
    return state.currentOrganizationId && state.currentOrganizationId !== ALL_ORGS ? state.currentOrganizationId : ALL_ORGS;
};

export const useNames = () => {
    const { state } = useData();
    return {
        org: (id?: string | null) => orgDisplayName(state.organizations.find(o => o.id === id)) || '—',
        orgShort: (id?: string | null) => {
            const o = state.organizations.find(x => x.id === id);
            return o ? (o.type === 'ENTREPRENEUR' ? o.name.split(' ')[0] : o.name) : '—';
        },
        cp: (id?: string | null) => state.counterparties.find(c => c.id === id)?.name || '—',
        wallet: (orgId?: string | null, walletId?: string | null) => {
            const w = state.organizations.find(o => o.id === orgId)?.accounts.find(a => a.id === walletId);
            return w ? (w.bankName || w.name || 'Счёт') : '—';
        },
    };
};

export const contractLabel = (c: Contract, orgShort: (id: string) => string) =>
    `${orgShort(c.organizationId)} · №${c.number} от ${formatDay(c.date)}`;

export const DocLink: React.FC<{ file: string | null; label?: string }> = ({ file, label = 'DOCX' }) =>
    file ? <a href={api.fileUrl(file)} className="inline-flex items-center gap-0.5 text-blue-600 hover:underline text-xs" target="_blank" rel="noreferrer">
        <FileDown size={12} /> {label}</a> : null;

export const Th: React.FC<{ children?: React.ReactNode; right?: boolean; className?: string }> = ({ children, right, className = '' }) =>
    <th className={`px-3 py-2 font-medium ${right ? 'text-right' : 'text-left'} ${className}`}>{children}</th>;

export const Td: React.FC<{ children?: React.ReactNode; right?: boolean; className?: string; colSpan?: number }> = ({ children, right, className = '', colSpan }) =>
    <td colSpan={colSpan} className={`px-3 py-2 ${right ? 'text-right' : ''} ${className}`}>{children}</td>;

export const errorText = (e: any) => e?.message || 'Ошибка';

// CSV для Excel: «;» и BOM, чтобы кириллица открылась без мастера импорта
export const downloadCsv = (name: string, rows: (string | number)[][]) => {
    const esc = (v: string | number) => { const s = String(v); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const blob = new Blob(['﻿' + rows.map(r => r.map(esc).join(';')).join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

export const csvMoney = (n: number) => (n || 0).toFixed(2).replace('.', ',');
